import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader } from "../components/DesignSystem";
import type { ID, StationeryItem, StationeryItemType } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useSubmitGuard } from "./common";
import { RequestQuoteAction } from "./RequestQuoteAction";

const ITEM_LABELS: Record<StationeryItemType, string> = {
  invitations: "Invitations",
  details_cards: "Details cards",
  rsvp_cards: "RSVP cards",
  place_cards: "Place cards",
  menus: "Menus",
  table_numbers: "Table numbers",
  seating_chart: "Seating chart",
  thank_you_cards: "Thank-you cards"
};

const ITEM_HINTS: Record<StationeryItemType, string> = {
  invitations: "One suite per invited household",
  details_cards: "One per invited household",
  rsvp_cards: "One per invited household",
  place_cards: "One per seated (or confirmed) guest",
  menus: "One per confirmed diner",
  table_numbers: "One per table",
  seating_chart: "One chart once tables exist",
  thank_you_cards: "One per invited household"
};

export function StationeryScreen() {
  const { workspace, loading, error, service } = usePlannerData();
  const [items, setItems] = React.useState<StationeryItem[] | null>(null);
  const [itemsLoading, setItemsLoading] = React.useState(false);
  const [itemsError, setItemsError] = React.useState<string | null>(null);
  const [overriding, setOverriding] = React.useState<StationeryItem | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<ID | null>(null);

  const load = React.useCallback(async () => {
    setItemsLoading(true);
    setItemsError(null);
    try {
      setItems(await service.stationery.getSummary());
    } catch (err) {
      setItemsError(errorMessage(err));
    } finally {
      setItemsLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    void service.analytics.track("stationery_opened").catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading stationery" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const reset = async (item: StationeryItem) => {
    setActionError(null);
    setBusyId(item.id);
    try {
      const updated = await service.stationery.resetToCalculated(item.id);
      setItems((prev) => (prev || []).map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const wholeWeddingItems = (items || []).filter((item) => !item.eventId);
  const eventGroups = workspace.events.map((event) => ({
    event,
    items: (items || []).filter((item) => item.eventId === event.id)
  }));

  return (
    <div>
      <PageHeader
        eyebrow="Design"
        title="Stationery"
        meta={[{ icon: "mail-open", label: "Whole wedding overview" }]}
        actions={<RequestQuoteAction defaultItems={["Invitations"]} />}
      />
      <div className="pob-pad">
        {itemsLoading && !items && <LoadingState label="Calculating stationery quantities" />}
        {itemsError && <ErrorState message={itemsError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}

        {items && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
            <StationeryGroup title="Whole wedding" items={wholeWeddingItems} busyId={busyId} onOverride={setOverriding} onReset={reset} />
            {eventGroups.map(({ event, items: eventItems }) => (
              <StationeryGroup key={event.id} title={event.name} items={eventItems} busyId={busyId} onOverride={setOverriding} onReset={reset} />
            ))}
          </div>
        )}
      </div>

      {overriding && (
        <OverrideDialog
          item={overriding}
          onClose={() => setOverriding(null)}
          onSaved={(updated) => {
            setItems((prev) => (prev || []).map((i) => (i.id === updated.id ? updated : i)));
            setOverriding(null);
          }}
        />
      )}
    </div>
  );
}

function StationeryGroup({ title, items, busyId, onOverride, onReset }: {
  title: string;
  items: StationeryItem[];
  busyId: ID | null;
  onOverride: (item: StationeryItem) => void;
  onReset: (item: StationeryItem) => void;
}) {
  return (
    <Card eyebrow={title} title={`${title} quantities`} padding="0">
      <DataTable
        rows={items}
        emptyLabel="Nothing to calculate yet"
        columns={[
          { key: "itemType", header: "Item", render: (row) => (
            <div>
              <span style={{ font: "var(--type-label)", display: "block" }}>{ITEM_LABELS[row.itemType]}</span>
              <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{ITEM_HINTS[row.itemType]}</span>
            </div>
          ) },
          { key: "quantity", header: "Quantity", width: 110, render: (row) => (
            <span style={{ font: "var(--type-label)" }}>{row.manualOverride ? row.overrideQuantity : row.calculatedQuantity}</span>
          ) },
          { key: "status", header: "", width: 140, render: (row) => row.manualOverride
            ? <Badge tone="accent">Manual · calc. {row.calculatedQuantity}</Badge>
            : <Badge tone="neutral">Calculated</Badge> },
          { key: "actions", header: "", width: 190, render: (row) => (
            <span style={{ display: "flex", gap: "var(--space-2)" }}>
              <Button size="sm" disabled={busyId === row.id} onClick={() => onOverride(row)}>Override</Button>
              {row.manualOverride && <Button size="sm" disabled={busyId === row.id} onClick={() => onReset(row)}>{busyId === row.id ? "…" : "Reset"}</Button>}
            </span>
          ) }
        ]}
      />
    </Card>
  );
}

function OverrideDialog({ item, onClose, onSaved }: { item: StationeryItem; onClose: () => void; onSaved: (item: StationeryItem) => void }) {
  const { service } = usePlannerData();
  const [quantity, setQuantity] = React.useState(String(item.manualOverride ? item.overrideQuantity : item.calculatedQuantity));
  const [reason, setReason] = React.useState(item.overrideReason || "");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    const parsed = Number(quantity);
    if (!Number.isFinite(parsed) || parsed < 0) { setFormError("Enter a quantity of 0 or more."); return; }
    try {
      const updated = await service.stationery.setOverride(item.id, parsed, reason.trim() || undefined);
      onSaved(updated);
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title={`Override ${ITEM_LABELS[item.itemType]}`}
      description={`Calculated quantity is currently ${item.calculatedQuantity}. Once you set a manual quantity, it will never be silently recalculated — you'll need to reset it yourself.`}
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save override"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Quantity"><Input value={quantity} type="number" onChange={(event) => setQuantity(event.target.value)} /></Field>
        <Field label="Reason (optional)"><Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="e.g. printing a buffer for late RSVPs" /></Field>
      </div>
    </Dialog>
  );
}
